import React from "react";
import { type GetStaticProps, type NextPage } from "next";
import {
  type Banner,
  type Page as SanityPage,
  getAnnouncementBanner,
  getPage,
  getStackedLogos,
  type Customer,
} from "lib/sanity";
import { Page } from "components/page";
import { HalfHero } from "components/half-hero";
import { StackedLogos } from "components/logos";
import SecurityPairs from "components/platform/security/security-pairs";
import { getPageMetaImage } from "utils";
import { SecurityFeatures } from "components/content";
import heroImage from "../../../public/assets/platform/security/hero.png";

type PageProps = {
  page: SanityPage;
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

const Security: NextPage<Readonly<PageProps>> = ({ page, announcementBanner, customerRows }) => {
  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <HalfHero
        eyebrow={page.eyebrow}
        title={page.title}
        description={page.description}
        image={heroImage}
        imageRatio={2 / 1}
        imageObjectFit="contain"
      />

      <StackedLogos customerRows={customerRows} />

      <SecurityFeatures />

      <SecurityPairs />
    </Page>
  );
};

export default Security;

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const [page, announcementBanner, customerRows] = await Promise.all([
    getPage("/platform/security"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      customerRows,
    },
    revalidate: 60,
  };
};
